export type ToolId =
  | "description"
  | "title"
  | "tags"
  | "keywords"
  | "ideas"
  | "seo"
  | "listing-studio";

export type OutputKey = "title" | "description" | "tags" | "keywords" | "ideas" | "plan";

export type ToolFieldType = "text" | "textarea" | "select" | "number";

export interface ToolField {
  name: string;
  label: string;
  type: ToolFieldType;
  placeholder: string;
  required?: boolean;
  options?: string[];
}

export interface ToolDefinition {
  id: ToolId;
  slug: string;
  legacyType?: string;
  title: string;
  shortTitle: string;
  eyebrow: string;
  description: string;
  actionLabel: string;
  outputKeys: OutputKey[];
  fields: ToolField[];
  starterInputs: Record<string, string | number>;
  promptFocus: string;
}

export interface ResearchTool {
  slug: string;
  title: string;
  section: "Brainstorm" | "Analyze";
  description: string;
}

const commonFields: ToolField[] = [
  {
    name: "productName",
    label: "Product name",
    type: "text",
    placeholder: "Handmade ceramic ring dish",
    required: true
  },
  {
    name: "audience",
    label: "Buyer or occasion",
    type: "text",
    placeholder: "Gift buyers, bridesmaids, minimalist home decor fans"
  },
  {
    name: "keywords",
    label: "Target keywords",
    type: "textarea",
    placeholder: "ceramic ring dish, bridesmaid gift, jewelry holder, handmade pottery"
  },
  {
    name: "tone",
    label: "Tone",
    type: "select",
    placeholder: "Warm and polished",
    options: ["Warm and polished", "Simple and direct", "Luxury boutique", "Playful", "Eco-conscious"]
  }
];

export const TOOLS: ToolDefinition[] = [
  {
    id: "description",
    slug: "description-generator",
    legacyType: "19",
    title: "Etsy Description Generator",
    shortTitle: "Description",
    eyebrow: "Listing copy",
    description: "Create a clear, keyword-rich Etsy listing description for buyers and search.",
    actionLabel: "Generate description",
    outputKeys: ["title", "description", "tags", "keywords"],
    fields: [
      ...commonFields,
      {
        name: "materials",
        label: "Materials and details",
        type: "textarea",
        placeholder: "Stoneware clay, clear glaze, hand-painted gold rim, 4 inches wide"
      },
      {
        name: "shippingNotes",
        label: "Care, shipping, or personalization notes",
        type: "textarea",
        placeholder: "Gift wrapping available. Each piece has small handmade variations."
      }
    ],
    starterInputs: {
      productName: "Handmade ceramic ring dish",
      audience: "Gift buyers and bridesmaids",
      keywords: "ceramic ring dish, jewelry holder, bridesmaid gift, handmade pottery",
      tone: "Warm and polished",
      materials: "Stoneware clay, clear glaze, hand-painted gold rim, 4 inches wide",
      shippingNotes: "Gift wrapping available. Each piece has small handmade variations."
    },
    promptFocus:
      "Write one Etsy-ready title, one concise listing description, 13 Etsy tags, and 8 keyword ideas. Keep claims realistic and do not invent live demand data."
  },
  {
    id: "title",
    slug: "title-generator",
    title: "Etsy Title Generator",
    shortTitle: "Title",
    eyebrow: "SEO title",
    description: "Draft a readable Etsy product title that balances keywords and buyer clarity.",
    actionLabel: "Generate title",
    outputKeys: ["title", "keywords"],
    fields: [
      ...commonFields,
      {
        name: "style",
        label: "Style or category",
        type: "text",
        placeholder: "Minimalist pottery, handmade home decor"
      }
    ],
    starterInputs: {
      productName: "Personalized birth flower necklace",
      audience: "Birthday gifts and Mother's Day shoppers",
      keywords: "birth flower necklace, personalized jewelry, birthday gift for her",
      tone: "Luxury boutique",
      style: "Gold filled floral jewelry"
    },
    promptFocus:
      "Return one Etsy title under 140 characters and supporting keyword phrases. Avoid keyword stuffing and do not use marketplace statistics."
  },
  {
    id: "tags",
    slug: "tag-generator",
    title: "Etsy Tag Generator",
    shortTitle: "Tags",
    eyebrow: "Search tags",
    description: "Generate Etsy tag ideas that fit the product, buyer, material, and occasion.",
    actionLabel: "Generate tags",
    outputKeys: ["tags", "keywords"],
    fields: [
      ...commonFields,
      {
        name: "attributes",
        label: "Attributes",
        type: "textarea",
        placeholder: "digital download, neutral colors, printable wall art, nursery decor"
      }
    ],
    starterInputs: {
      productName: "Printable nursery wall art set",
      audience: "New parents and baby shower gift buyers",
      keywords: "nursery prints, safari wall art, printable decor",
      tone: "Simple and direct",
      attributes: "Digital download, neutral colors, set of 3, safari animals"
    },
    promptFocus:
      "Return 13 Etsy tags, each 20 characters or less when possible, plus related keyword phrases. Do not claim search volume."
  },
  {
    id: "keywords",
    slug: "keyword-generator",
    title: "Etsy Keyword Generator",
    shortTitle: "Keywords",
    eyebrow: "Keyword ideas",
    description: "Expand a product into practical Etsy keyword clusters and listing angles.",
    actionLabel: "Generate keywords",
    outputKeys: ["keywords", "tags"],
    fields: [
      ...commonFields,
      {
        name: "marketAngle",
        label: "Market angle",
        type: "textarea",
        placeholder: "Eco-friendly gifts, minimalist desk decor, holiday stocking stuffer"
      }
    ],
    starterInputs: {
      productName: "Reusable linen bread bag",
      audience: "Sustainable kitchen shoppers",
      keywords: "linen bread bag, reusable bread storage, eco kitchen gift",
      tone: "Eco-conscious",
      marketAngle: "Plastic-free kitchen, farmhouse pantry, housewarming gift"
    },
    promptFocus:
      "Return keyword clusters and Etsy tag candidates. Mark them as ideas, not verified volume or ranking data."
  },
  {
    id: "ideas",
    slug: "product-idea-generator",
    title: "Etsy Product Idea Generator",
    shortTitle: "Ideas",
    eyebrow: "Product planning",
    description: "Turn a niche or material into realistic Etsy product concepts.",
    actionLabel: "Generate ideas",
    outputKeys: ["ideas", "keywords"],
    fields: [
      {
        name: "niche",
        label: "Niche or material",
        type: "text",
        placeholder: "Laser cut wood decor",
        required: true
      },
      {
        name: "audience",
        label: "Buyer",
        type: "text",
        placeholder: "Wedding planners, new homeowners, holiday shoppers"
      },
      {
        name: "constraints",
        label: "Constraints",
        type: "textarea",
        placeholder: "Low shipping cost, can be personalized, small batch production"
      }
    ],
    starterInputs: {
      niche: "Laser cut wood decor",
      audience: "New homeowners and wedding gift buyers",
      constraints: "Low shipping cost, can be personalized, simple to photograph"
    },
    promptFocus:
      "Return practical Etsy product ideas with brief positioning and keyword angles. Do not invent live demand."
  },
  {
    id: "seo",
    slug: "seo-planner",
    title: "Etsy SEO Planner",
    shortTitle: "SEO Planner",
    eyebrow: "Listing plan",
    description: "Create a lightweight SEO improvement plan for one Etsy listing.",
    actionLabel: "Generate SEO plan",
    outputKeys: ["plan", "keywords", "tags"],
    fields: [
      ...commonFields,
      {
        name: "currentDescription",
        label: "Current listing draft",
        type: "textarea",
        placeholder: "Paste your current description, title, or notes"
      }
    ],
    starterInputs: {
      productName: "Boho macrame plant hanger",
      audience: "Apartment decorators and plant lovers",
      keywords: "macrame plant hanger, boho plant decor, hanging planter",
      tone: "Warm and polished",
      currentDescription: "Handmade cotton plant hanger for indoor plants."
    },
    promptFocus:
      "Return a practical Etsy SEO plan with title, description, tag, photo, and buyer-intent recommendations. Do not claim live ranking impact."
  },
  {
    id: "listing-studio",
    slug: "listing-studio",
    title: "Listing Studio",
    shortTitle: "Listing Studio",
    eyebrow: "Full listing",
    description: "Generate a title, description, tags, keywords, and improvement plan together.",
    actionLabel: "Generate listing set",
    outputKeys: ["title", "description", "tags", "keywords", "plan"],
    fields: [
      ...commonFields,
      {
        name: "materials",
        label: "Materials and details",
        type: "textarea",
        placeholder: "Soy wax, cotton wick, amber jar, lavender and cedar scent"
      },
      {
        name: "pricePoint",
        label: "Price point",
        type: "text",
        placeholder: "$24 giftable candle"
      }
    ],
    starterInputs: {
      productName: "Lavender cedar soy candle",
      audience: "Housewarming and self-care gift buyers",
      keywords: "soy candle, lavender candle, housewarming gift, amber jar candle",
      tone: "Warm and polished",
      materials: "Soy wax, cotton wick, amber jar, lavender and cedar scent",
      pricePoint: "$24 giftable candle"
    },
    promptFocus:
      "Return a complete Etsy listing set: title, description, 13 tags, keyword ideas, and a concise SEO checklist. Keep every claim realistic."
  }
];

export const RESEARCH_TOOLS: ResearchTool[] = [
  {
    slug: "best-sellers",
    title: "Best Sellers",
    section: "Brainstorm",
    description: "Visible in the workspace, but live Etsy sales data is not connected in this free restoration."
  },
  {
    slug: "etsy-trends",
    title: "Etsy Trends",
    section: "Brainstorm",
    description: "Trend navigation is restored without pretending to have real-time marketplace data."
  },
  {
    slug: "niche-finder",
    title: "Niche Finder",
    section: "Brainstorm",
    description: "Use product ideas and keywords for now; a real discovery feed requires a separate data source."
  },
  {
    slug: "shop-analyzer",
    title: "Shop Analyzer",
    section: "Analyze",
    description: "Shop analysis needs an Etsy data connection and is therefore shown as a disabled state."
  },
  {
    slug: "listing-analyzer",
    title: "Listing Analyzer",
    section: "Analyze",
    description: "Listing analysis is parked until a real listing fetcher or paste-based analyzer is added."
  }
];

export function findTool(value: string | null | undefined): ToolDefinition | undefined {
  if (!value) {
    return undefined;
  }

  return TOOLS.find((tool) => tool.id === value || tool.slug === value || tool.legacyType === value);
}

export function toolFromLocation(pathname: string, search: string): ToolDefinition | undefined {
  if (pathname === "/document-create") {
    const type = new URLSearchParams(search).get("type");
    return findTool(type) ?? findTool("description");
  }

  const match = pathname.match(/^\/tools\/([^/]+)/);
  if (!match) {
    return undefined;
  }

  return findTool(match[1]);
}

export function findResearchTool(pathname: string): ResearchTool | undefined {
  const match = pathname.match(/^\/tools\/([^/]+)/);
  if (!match) {
    return undefined;
  }

  return RESEARCH_TOOLS.find((tool) => tool.slug === match[1]);
}

export function defaultTool(): ToolDefinition {
  return TOOLS[0];
}

export function outputInstruction(tool: ToolDefinition): string {
  const keys = tool.outputKeys.map((key) => {
    if (key === "title" || key === "description") {
      return `"${key}": string`;
    }

    return `"${key}": string[]`;
  });

  return `Return strict JSON only with this shape: { ${keys.join(", ")} }.`;
}
